import angular from 'angular'

import {
  wrap, clamp, slice, first, isNull, isArray,
  constant, reduceRight, chunk as chunkArray
} from 'lodash-es'

import { addToArray, delFromArray, replaceArray, unshiftToArray } from '../utils/arrayh'
import { noargs, defaultTo, isEmptyArray, isPosInteger, isNotEmptyArray } from '../utils/bits'
import { getDiffPosts, getIdsByPosts, dropPostsByIds, getDuplicatedPosts } from '../utils/posth'

import envRaw from '../utils/env-raw'
import Emitter from '../utils/emitter'
import postFlag from '../utils/post-flag'
import callTracker from '../utils/call-tracker'
import postViewIndex from '../utils/post-view-index'
import postModelIndex from '../utils/post-model-index'

angular
  .module('ggs.gallery')
  .factory('PostListManager', PostListManagerProvider)

function PostListManagerProvider(
  $q,
  qh,
  $log,
  $timeout,
  $interval,
  waitAngular,
  DefineMethod,
  DefineHandler) {

  const CHUNK_MAX_SIZE = 8
  const CHUNK_ADD_DELAY = 0.5e3

  const DEFAULT_ABOVE_TIMEOUT_DELAYS = [2e3, 5e3, 10e3, 30e3, 60e3, 2 * 60e3, 5 * 60e3, 10 * 60e3]
  const DEFAULT_BELOW_INTERVAL_DELAY = 2e3

  return function PostListManager(
    properties,
    topPosts,
    rpcGetAbovePosts,
    rpcGetBelowPosts,
    stayInPostQueue,
    formatPost,
    mainCanFetchPosts,
    canFetchAbovePosts,
    canFetchBelowPosts) {

    const sign = 'galleryMain::postListManager'
    const wrapM = DefineMethod(sign)
    const wrapH = DefineHandler(sign)

    // emitters
    const fetchAboveEmitter = Emitter(`${sign}::fetchAboveEmitter`)
    const fetchBelowEmitter = Emitter(`${sign}::fetchBelowEmitter`)
    const preloadBelowEmitter = Emitter(`${sign}::preloadBelowEmitter`)
    const setupTopPostsEmitter = Emitter(`${sign}::setupTopPostsEmitter`)

    // summaries
    const zeroPosts = () => true
      && isPostListReady()
      && isPostListEmpty()

    const somePostDone = () =>
      !isActualDonePostListEmpty()

    const everyPostFail = () => true
      && isPostListReady()
      && isAllBelowPostsFetched()
      && isAllActualPostsFailed()

    const hasPreloadedPosts = () => false
      || hasPreloadedAbovePosts()
      || hasPreloadedBelowPosts()

    // fetch
    let fetchAboveTimeoutId = null
    const fetchAboveTimeoutDelays = defaultTo(envRaw.aboveTimeoutDelays, DEFAULT_ABOVE_TIMEOUT_DELAYS)

    let _fetchAboveTimeoutIndex = null

    const getFetchAboveTimeoutDelay = () =>
      defaultTo(fetchAboveTimeoutDelays[_fetchAboveTimeoutIndex], first(fetchAboveTimeoutDelays))

    const nextFetchAboveTimeoutDelay = () => {
      _fetchAboveTimeoutIndex = clamp(defaultTo(_fetchAboveTimeoutIndex, 0) + 1, 0, fetchAboveTimeoutDelays.length - 1)
      return getFetchAboveTimeoutDelay()
    }

    const resetFetchAboveTimeoutDelay = () => {
      _fetchAboveTimeoutIndex = 0
      return getFetchAboveTimeoutDelay()
    }

    let fetchBelowIntervalId = null
    const fetchBelowIntervalDelay = defaultTo(envRaw.belowIntervalDelay, DEFAULT_BELOW_INTERVAL_DELAY)

    const fetchAboveLast = {
      max: null,
      limit: null,
      running: null,
      revision: null,
      preloaded: null,
      responseCount: null
    }

    const fetchBelowLast = {
      max: null,
      skip: null,
      last: null,
      limit: null,
      running: null,
      revision: null,
      preloaded: null,
      responseCount: null
    }

    const preloadAboveLast = {
      max: null,
      limit: null,
      posts: null,
      running: null,
      revision: null,
      duplicate: null
    }

    const preloadBelowLast = {
      max: null,
      skip: null,
      last: null,
      limit: null,
      posts: null,
      running: null,
      revision: null,
      duplicate: null
    }

    // pendingChunks
    const _pendingChunks = []

    const addToPendingChunks = (pendingChunk) =>
      addToArray(_pendingChunks, pendingChunk)

    const delFromPendingChunks = (pendingChunk) =>
      delFromArray(_pendingChunks, pendingChunk)

    const rejectPendingChunks = () => {
      _pendingChunks.forEach((pendingChunk) => pendingChunk.cancel())
      _pendingChunks.length = 0
    }

    const getPendingChunksSize = () =>
      _pendingChunks.length

    const isPendingChunksEmpty = () =>
      getPendingChunksSize() === 0

    // revision
    let _revision = 0

    const getRevision = () =>
      _revision

    const updateRevision = () =>
      ++_revision

    const hasActualRevision = (post) =>
      post.revision === _revision

    const hasOutdatedRevision = (post) =>
      post.revision !== _revision

    const getPostsWithActualRevision = (posts) =>
      posts.filter(hasActualRevision)

    const getPostsWithOutdatedRevision = (posts) =>
      posts.filter(hasOutdatedRevision)

    const assignActualRevision = (post) => {
      if (isArray(post)) {
        post.forEach(assignActualRevision)
      } else {
        post.revision = _revision
      }
      return post
    }

    // unprocessed ids
    const _unprocessedAboveIds = []
    const _unprocessedBelowIds = []

    const getUnprocessedAboveList = () =>
      _unprocessedAboveIds

    const getUnprocessedBelowList = () =>
      _unprocessedBelowIds

    const getUnprocessedAboveListSize = () =>
      _unprocessedAboveIds.length

    const getUnprocessedBelowListSize = () =>
      _unprocessedBelowIds.length

    const isUnprocessedAboveListEmpty = () =>
      getUnprocessedAboveListSize() === 0

    const isUnprocessedBelowListEmpty = () =>
      getUnprocessedBelowListSize() === 0

    const addToUnprocessedAboveList = (post) =>
      addToArray(_unprocessedAboveIds, post.id)

    const addToUnprocessedBelowList = (post) =>
      addToArray(_unprocessedBelowIds, post.id)

    const delFromUnprocessedLists = (post) => {
      delFromArray(_unprocessedAboveIds, post.id)
      delFromArray(_unprocessedBelowIds, post.id)
    }

    const dropUnprocessedLists = () => {
      _unprocessedAboveIds.length = 0
      _unprocessedBelowIds.length = 0
    }

    // postList
    const _postList = []

    const contains = (post) =>
      _postList.indexOf(post) !== -1

    const getPostList = () =>
      _postList

    const getPostListSize = () =>
      _postList.length

    const isPostListEmpty = () =>
      getPostListSize() === 0

    const getSkip = () =>
      getActualPostListSize()

    const getMaxId = () => {
      if (!properties.recent_order) {
        return properties.max_id
      }

      let max_id = null
      for (const post of _postList) {
        const isBigger = post.id > max_id || isNull(max_id)
        if (isBigger && hasActualRevision(post)) {
          max_id = post.id
        }
      }

      if (isNull(max_id) && isNull(properties.max_id)) {
        return null
      } else if (properties.max_id > max_id) {
        return properties.max_id
      } else {
        return max_id
      }
    }

    const getLastId = () => {
      for (let index = _postList.length - 1; index !== -1; index--) {
        const post = _postList[index]
        if (hasActualRevision(post)) {
          return post.id
        }
      }
      return null
    }

    const getActualPostList = () =>
      getPostsWithActualRevision(_postList)

    const getActualPostListSize = () => {
      let counter = 0
      for (const post of _postList) {
        if (hasActualRevision(post)) {
          counter++
        }
      }
      return counter
    }

    const getOutdatedPostList = () =>
      getPostsWithOutdatedRevision(_postList)

    const getOutdatedPostListSize = () => {
      let counter = 0
      for (const post of _postList) {
        if (!hasActualRevision(post)) {
          counter++
        }
      }
      return counter
    }

    const getActualDonePostList = () =>
      getActualPostList().filter(postFlag.isDone)

    const getActualDonePostListSize = () => {
      let counter = 0
      for (const post of _postList) {
        if (hasActualRevision(post) && postFlag.isDone(post)) {
          counter++
        }
      }
      return counter
    }

    const isActualDonePostListEmpty = () => {
      for (const post of _postList) {
        if (hasActualRevision(post) && postFlag.isDone(post)) {
          return false
        }
      }
      return true
    }

    const getLastActualDonePost = () => {
      for (let index = _postList.length - 1; index !== -1; index--) {
        const post = _postList[index]
        if (hasActualRevision(post) && postFlag.isDone(post)) {
          return post
        }
      }
      return null
    }

    const getActualFailPostListSize = () => {
      let counter = 0
      for (const post of _postList) {
        if (hasActualRevision(post) && postFlag.isFail(post)) {
          counter++
        }
      }
      return counter
    }

    const isAllActualPostsFailed = () =>
      getActualPostListSize() === getActualFailPostListSize()

    const filterNewPostsByPostList = (posts) =>
      getDiffPosts(posts, _postList)

    const filterOutPostsByPostList = (posts) =>
      getDiffPosts(_postList, posts)

    const filterDupPostsByPostList = (posts) =>
      getDuplicatedPosts(posts, _postList)

    const delFromPostList = (posts) => {
      for (const post of posts) {
        delFromArray(_postList, post)
        delFromUnprocessedLists(post)
      }
      return posts
    }

    const actualPrependPostList = (post) => {
      if (isArray(post)) {
        post.forEach(actualPrependPostList)
      } else if (hasActualRevision(post)) {
        unshiftToArray(_postList, post)
        addToUnprocessedAboveList(post)
      }
      return post
    }

    const actualAppendPostList = (post) => {
      if (isArray(post)) {
        post.forEach(actualAppendPostList)
      } else if (hasActualRevision(post)) {
        addToArray(_postList, post)
        addToUnprocessedBelowList(post)
      }
      return post
    }

    const renewPostList = (posts) => {
      const postListIds = getIdsByPosts(_postList)
      for (const newPost of posts) {
        const oldPostIndex = postListIds.indexOf(newPost.id)
        if (oldPostIndex !== -1) {
          const oldPost = _postList[oldPostIndex]
          oldPost.revision = newPost.revision
          oldPost.modelIndex = newPost.modelIndex
          oldPost.votes_count = newPost.votes_count
        }
      }
      return posts
    }

    const cleanupPostList = () => {
      const actual = getActualPostList()
      const processed = actual.filter(postFlag.isProcessed)
      const sorted = processed.sort(postModelIndex.diffDesc)
      const indexed = postViewIndex.reIndex(sorted)
      replaceArray(_postList, indexed)
      dropUnprocessedLists()
      return _postList
    }

    const delUnprocessedFromPostList = () => {
      dropPostsByIds(_postList, getUnprocessedAboveList())
      dropPostsByIds(_postList, getUnprocessedBelowList())
      dropUnprocessedLists()
      return _postList
    }

    const delDeniedFromPostList = () => {
      delFromPostList(_postList.filter(postFlag.isDeny))
      return _postList
    }

    // limits
    const isTopLimitReached = () => {
      const size = getActualDonePostListSize()
      const limit = properties.top_posts_limit
      return size >= limit
    }

    const isGlobalLimitReached = () => {
      const size = getActualDonePostListSize()
      const limit = properties.global_posts_limit
      return isPosInteger(limit)
        ? size >= limit
        : false
    }

    const isCurrentLimitReached = () =>
      mainCanFetchPosts()
        ? isGlobalLimitReached()
        : isTopLimitReached()

    const getDynamicTopLimit = () => {
      const size = getActualDonePostListSize()
      const limit = properties.top_posts_limit
      return Math.max(0, limit - size)
    }

    const getDynamicAboveLimit = () => {
      if (!mainCanFetchPosts()) {
        const dynamicTopLimit = getDynamicTopLimit()
        return clamp(dynamicTopLimit, 0, properties.above_posts_limit)
      }

      return properties.above_posts_limit
    }

    const getDynamicBelowLimit = () => {
      if (!mainCanFetchPosts()) {
        const dynamicTopLimit = getDynamicTopLimit()
        return clamp(dynamicTopLimit, 0, properties.below_posts_limit)
      }

      const size = getActualDonePostListSize()
      const limit = properties.global_posts_limit
      return isPosInteger(limit)
        ? clamp(limit - size, 0, properties.below_posts_limit)
        : properties.below_posts_limit
    }

    const getDynamicAboveThreshold = () =>
      Math.ceil(getDynamicAboveLimit() / 2)

    const getDynamicBelowThreshold = () =>
      Math.ceil(getDynamicBelowLimit() / 2)

    const isFitAboveThreshold = () =>
      getUnprocessedAboveListSize() < getDynamicAboveThreshold()

    const isFitBelowThreshold = () =>
      getUnprocessedBelowListSize() < getDynamicBelowThreshold()

    const cutPostsByTopLimit = (posts) => {
      const limit = getDynamicTopLimit()
      const sliced = slice(posts, 0, limit)
      return sliced
    }

    // isPostListReady
    let _postListReady = false

    const markTopPostListAsReady = (topPostList) => {
      _postListReady = true
      return topPostList
    }

    const isPostListReady = () =>
      _postListReady

    // fetchAbovePosts/preloadAbovePosts/prependPostList
    const fetchAbovePosts = callTracker(wrapM('fetchAbovePosts', () => {
      if (fetchAboveLast.running) {
        return []
      }

      const abovePostsMax = getMaxId()
      const abovePostsLimit = getDynamicAboveLimit()
      if (abovePostsLimit === 0) {
        return []
      }

      fetchAboveLast.running = true
      fetchAboveLast.preloaded = false
      return qh
        .try(() => {
          if (true
            && preloadAboveLast.max === abovePostsMax
            && preloadAboveLast.limit >= abovePostsLimit
            && preloadAboveLast.running === false
            && preloadAboveLast.revision === getRevision()
            && isNotEmptyArray(preloadAboveLast.posts)) {
            $log.debug(
              `${sign}#fetchAbovePosts: will use list of preloaded posts!\
               \n\t preloadAboveLast.max: ${preloadAboveLast.max}\
               \n\t preloadAboveLast.limit: ${preloadAboveLast.limit}\
               \n\t preloadAboveLast.revision: ${preloadAboveLast.revision}\
               \n\t preloadAboveLast.posts.length: ${preloadAboveLast.posts.length}`)
            fetchAboveLast.preloaded = true
            return slice(preloadAboveLast.posts, -1 * abovePostsLimit)
          } else {
            return rpcGetAbovePosts(abovePostsMax, abovePostsLimit)
          }
        })
        .then(assignActualRevision)
        .then(filterNewPostsByPostList)
        .then(prependPostList)
        .then(qh.tap((abovePosts) => {
          fetchAboveLast.max = abovePostsMax
          fetchAboveLast.limit = abovePostsLimit
          fetchAboveLast.revision = getRevision()
          fetchAboveLast.responseCount = abovePosts.length
        }))
        .finally(() => {
          fetchAboveLast.running = false
          fetchAboveEmitter.emit()
        })
    }))

    const hasPreloadedAbovePosts = () => true
      && preloadAboveLast.max === getMaxId()
      && preloadAboveLast.revision === getRevision()
      && isNotEmptyArray(preloadAboveLast.posts)

    const preloadAbovePosts = callTracker(wrapM('preloadAbovePosts', () => {
      if (preloadAboveLast.running) {
        return []
      }

      const currentMax = getMaxId()
      const currentLimit = properties.above_posts_limit
      const duplicate = true
        && preloadAboveLast.max === currentMax
        && preloadAboveLast.limit >= currentLimit
        && preloadAboveLast.revision === getRevision()
        && isNotEmptyArray(preloadAboveLast.posts)

      if (duplicate) {
        preloadAboveLast.duplicate = true
        return preloadAboveLast.posts
      }

      preloadAboveLast.running = true
      preloadAboveLast.duplicate = false
      return rpcGetAbovePosts(currentMax, currentLimit).then(qh.tap((preloadedPosts) => {
        preloadAboveLast.max = currentMax
        preloadAboveLast.limit = currentLimit
        preloadAboveLast.posts = preloadedPosts
        preloadAboveLast.revision = getRevision()
        $log.debug(`${sign}#preloadAbovePosts: preload done!\
          \n\t preloadAboveLast.max: ${preloadAboveLast.max}\
          \n\t preloadAboveLast.limit: ${preloadAboveLast.limit}\
          \n\t preloadAboveLast.revision: ${preloadAboveLast.revision}\
          \n\t preloadAboveLast.posts.length: ${preloadAboveLast.posts.length}`)
      })).finally(() => {
        preloadAboveLast.running = false
      })
    }))

    const prependPostList = wrapM('prependPostList',
      (abovePosts) => {
        const chunks = chunkArray(abovePosts, CHUNK_MAX_SIZE)
        return reduceRight(chunks, (prev, chunk, index) => {
          const pendingChunk = qh.delay(CHUNK_ADD_DELAY * index)
          addToPendingChunks(pendingChunk)
          return prev
            .then(constant(pendingChunk))
            .then(wrap(pendingChunk, delFromPendingChunks))
            .then(wrap(chunk, formatPost.above))
            .then(wrap(chunk, prependChunkToPostList))
            .catch(qh.catchCanceled(() => {
              $log.warn(`${sign}#prependPostList: pendingChunk canceled!`)
            }))
        }, $q.resolve()).then(() => {
          return abovePosts
        })
      })

    const prependChunkToPostList = wrapM('prependChunkToPostList', (chunk) => {
      return reduceRight(chunk, (prev, post) => {
        return prev.then(wrap(post, actualPrependPostList)).then(waitAngular)
      }, $q.resolve())
    })

    // fetchBelowPosts/preloadBelowPosts/appendPostList
    const isAllBelowPostsFetched = () => true
      && fetchBelowLast.last === getLastId()
      && fetchBelowLast.revision === getRevision()
      && fetchBelowLast.responseCount === 0

    const fetchBelowPosts = callTracker(wrapM('fetchBelowPosts', () => {
      if (fetchBelowLast.running) {
        return []
      }

      if (isAllBelowPostsFetched()) {
        return []
      }

      const belowPostsMax = getMaxId()
      const belowPostsSkip = getSkip()
      const belowPostsLast = getLastId()
      const belowPostsLimit = getDynamicBelowLimit()
      if (belowPostsLimit === 0) {
        return []
      }

      fetchBelowLast.running = true
      return qh
        .try(() => {
          if (true
              && preloadBelowLast.last === belowPostsLast
              && preloadBelowLast.limit >= belowPostsLimit
              && preloadBelowLast.running === false
              && preloadBelowLast.revision === getRevision()) {
            $log.debug(
              `${sign}#fetchBelowPosts: will use list of preloaded posts!\
               \n\t preloadBelowLast.max: ${preloadBelowLast.max}\
               \n\t preloadBelowLast.skip: ${preloadBelowLast.skip}\
               \n\t preloadBelowLast.last: ${preloadBelowLast.last}\
               \n\t preloadBelowLast.limit: ${preloadBelowLast.limit}\
               \n\t preloadBelowLast.revision: ${preloadBelowLast.revision}\
               \n\t preloadBelowLast.posts.length: ${preloadBelowLast.posts.length}`)
            fetchBelowLast.preloaded = true
            return slice(preloadBelowLast.posts, 0, belowPostsLimit)
          } else {
            fetchBelowLast.preloaded = false
            return rpcGetBelowPosts(belowPostsMax, belowPostsSkip, belowPostsLimit)
          }
        })
        .then(assignActualRevision)
        .then(filterNewPostsByPostList)
        .then(appendPostList)
        .then(qh.tap((belowPosts) => {
          fetchBelowLast.max = belowPostsMax
          fetchBelowLast.skip = belowPostsSkip
          fetchBelowLast.last = belowPostsLast
          fetchBelowLast.limit = belowPostsLimit
          fetchBelowLast.revision = getRevision()
          fetchBelowLast.responseCount = belowPosts.length
        }))
        .finally(() => {
          fetchBelowLast.running = false
          fetchBelowEmitter.emit()
        })
    }))

    const hasPreloadedBelowPosts = () => true
      && ranPreloadedBelowPosts()
      && isNotEmptyArray(preloadBelowLast.posts)

    const ranPreloadedBelowPosts = () => true
      && preloadBelowLast.last === getLastId()
      && preloadBelowLast.revision === getRevision()

    const preloadBelowPosts = callTracker(wrapM('preloadBelowPosts', () => {
      if (preloadBelowLast.running) {
        return []
      }

      const currentMax = getMaxId()
      const currentSkip = getSkip()
      const currentLast = getLastId()
      const currentLimit = properties.below_posts_limit
      const duplicate = true
        && preloadBelowLast.last === currentLast
        && preloadBelowLast.limit >= currentLimit
        && preloadBelowLast.revision === getRevision()
        && isArray(preloadBelowLast.posts)

      if (duplicate) {
        preloadBelowLast.duplicate = true
        return preloadBelowLast.posts
      }

      preloadBelowLast.running = true
      preloadBelowLast.duplicate = false
      return rpcGetBelowPosts(currentMax, currentSkip, currentLimit).then(qh.tap((preloadedPosts) => {
        preloadBelowLast.max = currentMax
        preloadBelowLast.skip = currentSkip
        preloadBelowLast.last = currentLast
        preloadBelowLast.limit = currentLimit
        preloadBelowLast.posts = preloadedPosts
        preloadBelowLast.revision = getRevision()
        $log.debug(`${sign}#preloadBelowPosts: preload done!\
          \n\t preloadBelowLast.max: ${preloadBelowLast.max}\
          \n\t preloadBelowLast.skip: ${preloadBelowLast.skip}\
          \n\t preloadBelowLast.last: ${preloadBelowLast.last}\
          \n\t preloadBelowLast.limit: ${preloadBelowLast.limit}\
          \n\t preloadBelowLast.revision: ${preloadBelowLast.revision}\
          \n\t preloadBelowLast.posts.length: ${preloadBelowLast.posts.length}`)
      })).finally(() => {
        preloadBelowLast.running = false
        preloadBelowEmitter.emit()
      })
    }))

    const appendPostList = wrapM('appendPostList',
      (belowPosts) => {
        const chunks = chunkArray(belowPosts, CHUNK_MAX_SIZE)
        return chunks.reduce((prev, chunk, index) => {
          const pendingChunk = qh.delay(CHUNK_ADD_DELAY * index)
          addToPendingChunks(pendingChunk)
          return prev
            .then(constant(pendingChunk))
            .then(wrap(pendingChunk, delFromPendingChunks))
            .then(wrap(chunk, formatPost.below))
            .then(wrap(chunk, appendChunkToPostList))
            .catch(qh.catchCanceled(() => {
              $log.warn(`${sign}#appendPostList: pendingChunk canceled!`)
            }))
        }, $q.resolve()).then(() => {
          return belowPosts
        })
      })

    const appendChunkToPostList = wrapM('appendChunkToPostList', (chunk) => {
      return chunk.reduce((prev, post) => {
        return prev.then(wrap(post, actualAppendPostList)).then(waitAngular)
      }, $q.resolve())
    })

    // iterateFetchAbove
    const iterateFetchAbove = callTracker(wrapM('iterateFetchAbove',
      stayInPostQueue.makeIfIdle(() => {
        if (!isPostListReady()) {
          return
        }

        if (!canFetchAbovePosts()) {
          return
        }

        const canFetch = mainCanFetchPosts()
          ? isFitAboveThreshold()
          : isUnprocessedAboveListEmpty() && !isTopLimitReached() && isAllBelowPostsFetched()

        return canFetch
          ? fetchAbovePosts()
          : preloadAbovePosts()
      })))

    // iterateFetchBelow
    const iterateFetchBelow = callTracker(wrapM('iterateFetchBelow',
      stayInPostQueue.makeIfIdle(() => {
        if (!isPostListReady()) {
          return
        }

        if (!canFetchBelowPosts()) {
          return
        }

        const canFetch = mainCanFetchPosts()
          ? isFitBelowThreshold() && !isGlobalLimitReached()
          : isUnprocessedBelowListEmpty() && !isTopLimitReached()

        return canFetch
          ? fetchBelowPosts()
          : preloadBelowPosts()
      })))

    // init
    const setupTopPosts = wrapM('setupTopPosts',
      stayInPostQueue.make(
        qh.makeFlow(
          constant(topPosts),
          cutPostsByTopLimit,
          assignActualRevision,
          appendPostList,
          markTopPostListAsReady,
          setupTopPostsEmitter.emit)))

    const setupAboveFetchLoop = wrapM('setupAboveFetchLoop', (abovePosts, reset) => {
      const delay = isNotEmptyArray(abovePosts) || reset
        ? resetFetchAboveTimeoutDelay()
        : isEmptyArray(abovePosts)
          ? nextFetchAboveTimeoutDelay()
          : getFetchAboveTimeoutDelay()
      const handler = wrapH('fetchAboveIntervalHandler', qh.makeFlow(iterateFetchAbove, setupAboveFetchLoop))
      fetchAboveTimeoutId = $timeout(noargs(handler), delay)
    })

    const resetAboveFetchLoop = wrapM('resetAboveFetchLoop', () => {
      $timeout.cancel(fetchAboveTimeoutId)
      return setupAboveFetchLoop(null, true)
    })

    const setupBelowFetchLoop = wrapM('setupBelowFetchLoop', () => {
      const handler = wrapH('fetchBelowIntervalHandler', iterateFetchBelow)
      fetchBelowIntervalId = $interval(noargs(handler), fetchBelowIntervalDelay)
    })

    const init = wrapM('init',
      qh.makeOver(
        setupTopPosts,
        setupAboveFetchLoop,
        setupBelowFetchLoop))

    // destroy
    const destroy = wrapM('destroy', () => {
      $timeout.cancel(fetchAboveTimeoutId)
      $interval.cancel(fetchBelowIntervalId)
      rejectPendingChunks()
    })

    // export
    return {
      init,
      destroy,
      // emitters
      fetchAboveEmitter,
      fetchBelowEmitter,
      preloadBelowEmitter,
      setupTopPostsEmitter,
      // summaries
      zeroPosts,
      somePostDone,
      everyPostFail,
      hasPreloadedPosts,
      // fetch delay
      getFetchAboveTimeoutDelay,
      fetchBelowIntervalDelay,
      fetchAboveLast,
      fetchBelowLast,
      preloadAboveLast,
      preloadBelowLast,
      // pending chunks
      rejectPendingChunks,
      getPendingChunksSize,
      isPendingChunksEmpty,
      // revision
      getRevision,
      updateRevision,
      assignActualRevision,
      // unprocessed
      getUnprocessedAboveListSize,
      getUnprocessedBelowListSize,
      isUnprocessedAboveListEmpty,
      isUnprocessedBelowListEmpty,
      delFromUnprocessedLists,
      // postList
      contains,
      getPostList,
      getPostListSize,
      isPostListEmpty,
      getMaxId,
      // postList - selects
      getActualPostList,
      getActualPostListSize,
      getOutdatedPostList,
      getOutdatedPostListSize,
      getActualDonePostList,
      getActualDonePostListSize,
      getLastActualDonePost,
      // postList - filters
      filterNewPostsByPostList,
      filterOutPostsByPostList,
      filterDupPostsByPostList,
      // postList - modifiers
      renewPostList,
      cleanupPostList,
      delUnprocessedFromPostList,
      delDeniedFromPostList,
      // limits
      cutPostsByTopLimit,
      // limits - get
      getDynamicTopLimit,
      getDynamicAboveLimit,
      getDynamicAboveThreshold,
      getDynamicBelowLimit,
      getDynamicBelowThreshold,
      isFitAboveThreshold,
      isFitBelowThreshold,
      // limits - check
      isTopLimitReached,
      isGlobalLimitReached,
      isCurrentLimitReached,
      // readyState
      isPostListReady,
      // fetchAbovePosts
      hasPreloadedAbovePosts,
      // fetchBelowPosts
      isAllBelowPostsFetched,
      hasPreloadedBelowPosts,
      ranPreloadedBelowPosts,
      appendPostList,
      // others
      iterateFetchAbove,
      iterateFetchBelow,
      resetAboveFetchLoop
    }
  }
}
